import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Upload, FileText, Globe, CloudUpload, Info, Plus, X, GitMerge } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";

const tabs = [
  { id: "url", label: "From URL", icon: Globe },
  { id: "paste", label: "Paste JSON", icon: FileText },
  { id: "upload", label: "Upload File", icon: Upload },
];

type InputSectionProps = {
  onFileSelected: (file: File) => void;
  onUrlFetch?: (url: string) => void;
  /** Called with 2+ URLs — parent fetches, merges, then previews */
  onMergeUrls?: (urls: string[]) => void;
  /** Pasted OpenAPI/Swagger JSON or YAML — preview only; parent stores text for convert */
  onPastedTextPreview?: (text: string) => void;
  isUploading?: boolean;
};

const InputSection = ({ onFileSelected, onUrlFetch, onMergeUrls, onPastedTextPreview, isUploading }: InputSectionProps) => {
  const [activeTab, setActiveTab] = useState("url");
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null);
  const [pastedSpec, setPastedSpec] = useState("");
  const [specUrls, setSpecUrls] = useState<string[]>([""]);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const filledUrls = specUrls.filter(u => u.trim().length > 0);
  const isMergeMode = filledUrls.length >= 2;
  const canAction = Boolean(onUrlFetch) && !isUploading && filledUrls.length > 0;

  const setUrl = (index: number, value: string) => {
    setSpecUrls(prev => prev.map((u, i) => i === index ? value : u));
  };

  const addUrl = () => setSpecUrls(prev => [...prev, ""]);

  const removeUrl = (index: number) => {
    setSpecUrls(prev => prev.length > 1 ? prev.filter((_, i) => i !== index) : prev);
  };

  const submitUrl = () => {
    const filled = specUrls.map(u => u.trim()).filter(Boolean);
    if (filled.length === 0) return;
    if (filled.length === 1) {
      onUrlFetch?.(filled[0]);
    } else {
      onMergeUrls?.(filled);
    }
  };

  const submitPastedText = () => {
    const t = pastedSpec.trim();
    if (!t) return;
    onPastedTextPreview?.(pastedSpec);
  };

  const handleSelectedFile = (file?: File) => {
    if (!file) return;
    setSelectedFileName(file.name);
    onFileSelected(file);
  };

  return (
    <section id="try-now" className="relative pt-8 pb-20 px-6 font-bold">
      <div className="container max-w-2xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10"
        >
          <h2 className="text-2xl md:text-3xl font-semibold tracking-tight mb-4 text-foreground">
            Upload Your OpenAPI Spec
          </h2>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="glass-strong rounded-2xl p-7 shadow-soft"
        >
          {/* Tabs */}
          <div className="flex gap-1 p-1 rounded-xl bg-muted/60 mb-7">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`relative flex-1 flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg text-sm font-medium transition-colors ${
                  activeTab === tab.id ? "text-foreground" : "text-muted-foreground hover:text-foreground/70"
                }`}
              >
                {activeTab === tab.id && (
                  <motion.div
                    layoutId="activeTab"
                    className="absolute inset-0 rounded-lg bg-card shadow-sm"
                    transition={{ type: "spring", stiffness: 400, damping: 30 }}
                  />
                )}
                <span className="relative z-10 flex items-center gap-2">
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </span>
              </button>
            ))}
          </div>

          {/* Tab content */}
          <AnimatePresence mode="wait">
            {activeTab === "upload" && (
              <motion.div
                key="upload"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                onDragOver={(e) => {
                  e.preventDefault();
                  setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setIsDragging(false);
                  handleSelectedFile(e.dataTransfer.files?.[0]);
                }}
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-xl p-10 text-center transition-colors cursor-pointer ${
                  isDragging ? "border-foreground/30 bg-muted/30" : "border-border hover:border-muted-foreground/30"
                }`}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json,.yaml,.yml"
                  className="hidden"
                  onChange={(e) => {
                    handleSelectedFile(e.target.files?.[0]);
                    e.currentTarget.value = "";
                  }}
                />
                <CloudUpload className="w-10 h-10 mx-auto mb-3 text-secondary-foreground" />
                <p className="text-foreground text-sm mb-1 font-semibold">
                  {selectedFileName
                    ? `Selected: ${selectedFileName}`
                    : "Drop your OpenAPI file here"}
                </p>
                <p className="text-xs text-muted-foreground">
                  {isUploading
                    ? "Uploading & parsing your spec…"
                    : "or click to browse • JSON / YAML supported"}
                </p>
              </motion.div>
            )}

            {activeTab === "paste" && (
              <motion.div
                key="paste"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
              >
                <textarea
                  value={pastedSpec}
                  onChange={(e) => setPastedSpec(e.target.value)}
                  placeholder='{"openapi": "3.0.0", "info": { "title": "My API" }, ...}'
                  className="w-full h-48 rounded-xl bg-muted/40 border border-border p-4 text-sm font-mono text-foreground placeholder:text-muted-foreground/50 resize-none focus:outline-none focus:ring-2 focus:ring-ring/20 transition"
                />
                <motion.button
                  type="button"
                  disabled={!onPastedTextPreview || isUploading || !pastedSpec.trim()}
                  whileHover={!isUploading && pastedSpec.trim() ? { scale: 1.02 } : undefined}
                  whileTap={!isUploading && pastedSpec.trim() ? { scale: 0.98 } : undefined}
                  onClick={() => submitPastedText()}
                  className="mt-4 w-full py-3 rounded-xl btn-neu-primary text-sm font-medium transition-all disabled:opacity-50 disabled:pointer-events-none"
                >
                  {isUploading ? "Parsing…" : "Parse & preview"}
                </motion.button>
              </motion.div>
            )}

            {activeTab === "url" && (
              <motion.div
                key="url"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
              >
                <div className="mb-2 flex items-center justify-between text-xs text-muted-foreground font-normal">
                  <span>Enter a direct OpenAPI JSON/YAML URL</span>
                  <Tooltip delayDuration={300}>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-border text-muted-foreground hover:bg-muted transition"
                      >
                        <Info className="h-3.5 w-3.5" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent
                      side="right"
                      align="center"
                      sideOffset={8}
                      className="z-50 max-w-[260px] bg-muted border border-border text-[11px] font-mono font-normal text-foreground/80 px-3 py-1.5 rounded-lg shadow-sm leading-snug whitespace-normal break-words"
                    >
                      Postman pages won&apos;t work. Export as JSON and upload the file instead.
                    </TooltipContent>
                  </Tooltip>
                </div>

                <div className="flex flex-col gap-2">
                  <AnimatePresence initial={false}>
                    {specUrls.map((url, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.18 }}
                        className="flex gap-2"
                      >
                        <div className="flex-1 relative">
                          <Globe className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <input
                            type="url"
                            value={url}
                            onChange={(e) => setUrl(index, e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && canAction) submitUrl();
                            }}
                            placeholder="https://api.example.com/openapi.json"
                            className="w-full rounded-xl bg-muted/40 border border-border pl-10 pr-4 py-3.5 text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-ring/20 transition"
                          />
                        </div>
                        {specUrls.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeUrl(index)}
                            className="flex-shrink-0 w-10 h-[50px] flex items-center justify-center rounded-xl border border-border text-muted-foreground hover:text-foreground hover:bg-muted/60 transition"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </motion.div>
                    ))}
                  </AnimatePresence>

                  <button
                    type="button"
                    onClick={addUrl}
                    className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition mt-1 w-fit"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    Add another spec to merge
                  </button>

                  <motion.button
                    type="button"
                    disabled={!canAction}
                    whileHover={canAction ? { scale: 1.02 } : undefined}
                    whileTap={canAction ? { scale: 0.98 } : undefined}
                    onClick={submitUrl}
                    className="mt-1 w-full py-3 rounded-xl btn-neu-primary text-sm font-medium transition-all disabled:opacity-50 disabled:pointer-events-none flex items-center justify-center gap-2"
                  >
                    {isUploading ? (
                      isMergeMode ? "Merging…" : "Fetching…"
                    ) : isMergeMode ? (
                      <><GitMerge className="w-4 h-4" /> Merge & Preview</>
                    ) : (
                      "Fetch & Preview"
                    )}
                  </motion.button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};

export default InputSection;
